import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import AddButtonInTopBar from "../components/AddButtonInTopBar";
import BreadCrumb from "../components/breadCrumb";
import ProductBreadCrumb from "../components/products/ProductBreadCrumb";
import DataGrid from "../components/common/DataGrid";
import PageContent from "../components/pageContent";
import TopBarWithBreadCrumb from "../components/TopBarWithBreadCrumb";
import BasisPopup from "../popups/BasisPopup";
import { addNotification } from "../redux/actions";
import { apiDeleteBasis, apiGetAllBases, apiGetAllBasesByProduct } from "../services/BasesService";
import { checkCrud } from "../helpers/userRolesHelper";
import { useTranslation } from "react-i18next";
import { FormatString } from "../helpers/stringHelpers";
import { TranslationProvider } from "../providers/TranslationContext";

interface Props {
    contextLevel: string;
}

const entityName = 'base';
const translationPrefix = 'Bases';
const translationModel = { prefix: translationPrefix };

const BazePage = (props: Props) => {
    const { t } = useTranslation(undefined, { keyPrefix: translationPrefix });
    const [loading, setLoading] = useState(true);
    const [productInfoSelected, setProductInfoSelected] = useState(false);
    const [data, setData] = useState({ rows: [] });
    const [basisPopupData, setBasisPopupData] = useState({
        visible: false,
        id: undefined,
    });

    const [canCreate, setCanCreate] = useState(false);
    const [canRead, setCanRead] = useState(false);
    const [canUpdate, setCanUpdate] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    const userInfo = useSelector((state: any) => state.userInfo);

    const setRoles = () => {
        setCanCreate(checkCrud(entityName, 'create', userInfo));
        setCanRead(checkCrud(entityName, 'read', userInfo));
        setCanUpdate(checkCrud(entityName, 'update', userInfo));
        setCanDelete(checkCrud(entityName, 'delete', userInfo));
    }

    useEffect(() => {
        setRoles();
    }, [userInfo.UserRoles])

    const dispatch = useDispatch();

    const selectedProductInfo = useSelector((state: any) => state.productInfo);
    useEffect(() => {
        loadData();
    }, [selectedProductInfo]);

    const loadData = () => {
        if (selectedProductInfo) {
            if (selectedProductInfo.productId) {
                setProductInfoSelected(true);
                apiGetAllBasesByProduct(selectedProductInfo.productId).then((responseData) => {
                    setData({
                        rows: responseData.data,
                    });

                    setLoading(false);
                });
            }
        }
    };

    const handleDeleteRow = (row) => {
        apiDeleteBasis(row.id)
            .then((responseData) => {
                dispatch(
                    addNotification({
                        text: FormatString(t('Popup.DeleteSuccess'), row.name, row.productName),
                        type: "success",
                    })
                );
                loadData();

                setLoading(false);
            })
            .catch((err: any) => {
                dispatch(
                    addNotification({
                        text: FormatString(t('Popup.DeleteFailure'), row.name, row.productName),
                        type: "error",
                    })
                );
            });
    };

    return (
        <TranslationProvider value={translationModel}>
            {basisPopupData.visible && (
                <BasisPopup
                    id={basisPopupData.id}
                    refreshData={() => loadData()}
                    handleCancel={() => {
                        setBasisPopupData({ visible: false, id: undefined });
                    }}
                />
            )}
            <TopBarWithBreadCrumb
                contextLevel={props.contextLevel}
                pageLevel={t('Label')}
                rightAreaComponent={
                    <AddButtonInTopBar
                        setPopupData={setBasisPopupData}
                        text={t('CreateText')}
                        enabled={canCreate}
                    />
                }
            />
            <PageContent>
                <>
                    <ProductBreadCrumb selectionOfProductIsRequired={true} />
                    {productInfoSelected &&
                        <DataGrid
                            loading={loading}
                            rows={data.rows}
                            showDeleteButton={canDelete}
                            showEditButton={canUpdate}
                            onDeleteRow={handleDeleteRow}
                            handleEditAction={(id: any) => {
                                setBasisPopupData({ visible: true, id: id });
                            }}
                            cols={[
                                {
                                    name: t('Fields.productName'),
                                    dataField: "productName",
                                    isLinkToItem: true,
                                    sortable: true,
                                    filterable: true,
                                    filterType: "dropdown",
                                },
                                {
                                    name: t('Fields.name'),
                                    dataField: "name",
                                    isLinkToItem: false,
                                    sortable: true,
                                    filterable: true,
                                    filterType: "text",
                                },
                                {
                                    name: t('Fields.density'),
                                    dataField: "density",
                                    sortable: true,
                                    filterable: true,
                                    filterType: "text",
                                },
                                { name: t('Fields.noteInternal'), dataField: "noteInternal" },
                                {
                                    name: t('Fields.createdOn'),
                                    fieldType: "datetime",
                                    dataField: "createdOn",
                                    sortable: true,
                                },
                                {
                                    name: t('Fields.modifiedOn'),
                                    fieldType: "datetime",
                                    dataField: "modifiedOn",
                                    sortable: true,
                                },
                            ]}
                        />
                    }
                </>
            </PageContent>
        </TranslationProvider>
    );
};

export default BazePage;
